import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/shared/AuthContext';
import { useTheme } from '../context/shared/ThemeContext';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { signIn, user, loadingAuth } = useAuth();
  const navigate = useNavigate();
  const { themeClasses } = useTheme();

  // Watch for successful authentication
  useEffect(() => {
    if (!loadingAuth && user) {
      navigate('/chat');
    }
  }, [user, loadingAuth, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    try {
      setError('');
      setLoading(true);
      await signIn(email, password);
      // Navigation will happen in useEffect when user is ready
    } catch (err) {
      setError('Failed to sign in');
      console.error('Login error:', err);
      setLoading(false);
    }
  };

  return (
    <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 ${themeClasses.mainBg}`}>
      <div className="max-w-md w-full mx-auto">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center mb-6">
            <span role="img" aria-label="brain" className="text-4xl mr-3">🧠</span>
            <h1 className={`text-3xl font-bold ${themeClasses.primaryText}`}>
              Welcome Back
            </h1>
          </div>
          <p className={`mt-2 text-sm ${themeClasses.secondaryText}`}>
            Sign in to continue your journey
          </p>
        </div>

        <div className={`${themeClasses.cardBg} rounded-lg shadow-sm px-8 py-8`}>
          {error && (
            <div className="mb-4 p-3 rounded bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-200 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label htmlFor="email" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
                Email
              </label>
              <input
                id="email"
                type="email"
                required
                autoComplete="username"
                className={`
                  mt-1 block w-full rounded-md
                  border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-gray-100
                  focus:ring-blue-500 focus:border-blue-500
                  shadow-sm
                `}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
              />
            </div>

            <div>
              <label htmlFor="password" className={`block text-sm font-medium ${themeClasses.primaryText}`}>
                Password
              </label>
              <input
                id="password"
                type="password"
                required
                autoComplete="current-password"
                className={`
                  mt-1 block w-full rounded-md
                  border-gray-300 dark:border-gray-600
                  bg-white dark:bg-gray-700
                  text-gray-900 dark:text-gray-100
                  focus:ring-blue-500 focus:border-blue-500
                  shadow-sm
                `}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className={`
                w-full flex justify-center py-2 px-4 rounded-md
                ${loading ? 'opacity-70 cursor-not-allowed' : ''}
                bg-blue-600 hover:bg-blue-700
                text-white font-medium
                focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500
                transition-colors
              `}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className={`text-sm ${themeClasses.secondaryText}`}>
              Don't have an account?{' '}
              <Link to="/signup" className="font-medium text-blue-600 hover:text-blue-500">
                Sign up
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;